import type {
  PromotionCouponCreatePayload,
  PromotionCouponUpdatePayload,
  PromotionCouponWithPromotion,
  PromotionCreatePayload,
  PromotionRow,
  PromotionUpdatePayload,
  PromotionWithScopes,
} from "@/modules/promotions/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import type { MarketingPushMonitorResponse } from "@/shared/notifications/marketing-push-monitor.types";

const base = `${config.apiUrl}/loyalty/promotions`;
const couponsBase = `${base}/coupons`;

export type ListPromotionsParams = {
  isActive?: "true" | "false";
  search?: string;
  limit?: number;
  offset?: number;
  sortField?: "name" | "startAt" | "endAt" | "priority";
  sortOrder?: "asc" | "desc";
};

export type ListPromotionsResult = {
  data: PromotionRow[];
  total: number;
};

export type PromotionsSummary = {
  activeCount: number;
  scheduledToday: number;
  expiringSoon: number;
};

export const promotionsApi = {
  getSummary: (): Promise<PromotionsSummary> =>
    fetcher.get<PromotionsSummary>(`${base}/summary`),

  list: (params?: ListPromotionsParams): Promise<ListPromotionsResult> => {
    const search = new URLSearchParams();
    if (params?.isActive) search.set("isActive", params.isActive);
    if (params?.search) search.set("search", params.search);
    if (params?.limit != null) search.set("limit", String(params.limit));
    if (params?.offset != null) search.set("offset", String(params.offset));
    if (params?.sortField) search.set("sortField", params.sortField);
    if (params?.sortOrder) search.set("sortOrder", params.sortOrder);
    const q = search.toString();
    return fetcher.get(`${base}${q ? `?${q}` : ""}`);
  },

  get: (id: string): Promise<PromotionWithScopes | null> =>
    fetcher.get<PromotionWithScopes | null>(`${base}/${id}`),

  create: (payload: PromotionCreatePayload): Promise<PromotionWithScopes> =>
    fetcher.post<PromotionWithScopes>(base, payload),

  update: (
    id: string,
    payload: PromotionUpdatePayload,
  ): Promise<PromotionWithScopes> =>
    fetcher.put<PromotionWithScopes>(`${base}/${id}`, payload),

  resendPush: (
    id: string,
  ): Promise<{ ok: true; enqueuedAt: string; promotion: PromotionWithScopes }> =>
    fetcher.post(`${base}/${id}/resend-push`, {}),

  pushMonitor: (id: string): Promise<MarketingPushMonitorResponse> =>
    fetcher.get(`${base}/${id}/push-monitor`),
};

export type ListCouponsParams = {
  promotionId?: string;
  isActive?: "true" | "false";
  search?: string;
  limit?: number;
  offset?: number;
  sortField?: "code" | "createdAt";
  sortOrder?: "asc" | "desc";
};

export type ListCouponsResult = {
  data: PromotionCouponWithPromotion[];
  total: number;
};

export type CouponsSummary = {
  topCode: string | null;
  lowInventory: number;
  expiringSoon: number;
};

export const couponsApi = {
  getSummary: (): Promise<CouponsSummary> =>
    fetcher.get<CouponsSummary>(`${couponsBase}/summary`),

  list: (params?: ListCouponsParams): Promise<ListCouponsResult> => {
    const search = new URLSearchParams();
    if (params?.promotionId) search.set("promotionId", params.promotionId);
    if (params?.isActive) search.set("isActive", params.isActive);
    if (params?.search) search.set("search", params.search);
    if (params?.limit != null) search.set("limit", String(params.limit));
    if (params?.offset != null) search.set("offset", String(params.offset));
    if (params?.sortField) search.set("sortField", params.sortField);
    if (params?.sortOrder) search.set("sortOrder", params.sortOrder);
    const q = search.toString();
    return fetcher.get(`${couponsBase}${q ? `?${q}` : ""}`);
  },

  get: (id: string): Promise<PromotionCouponWithPromotion | null> =>
    fetcher.get<PromotionCouponWithPromotion | null>(`${couponsBase}/${id}`),

  create: (
    payload: PromotionCouponCreatePayload,
  ): Promise<PromotionCouponWithPromotion> =>
    fetcher.post<PromotionCouponWithPromotion>(couponsBase, payload),

  update: (
    id: string,
    payload: PromotionCouponUpdatePayload,
  ): Promise<PromotionCouponWithPromotion> =>
    fetcher.put<PromotionCouponWithPromotion>(`${couponsBase}/${id}`, payload),

  toggleActive: (
    id: string,
    isActive: boolean,
  ): Promise<PromotionCouponWithPromotion> =>
    fetcher.patch<PromotionCouponWithPromotion>(
      `${couponsBase}/${id}/toggle-active`,
      { isActive },
    ),

  delete: (id: string): Promise<{ id: string }> =>
    fetcher.delete<{ id: string }>(`${couponsBase}/${id}`),
};
